import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
  Textarea,
  Tabs,
  Tab,
  Tooltip,
} from '@nextui-org/react';
import { useLocation } from 'wouter';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import useUserStore from 'common/src/stores/useUserStore';
import useDataStore from 'common/src/stores/useDataStore';
import useFilterStore from 'common/src/stores/useFiltersStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import useExporterLabelsStore from '../../store/useExporterLabelsStore';
import { Alert, EmptyState, LoadingState } from '../ui';
import FreeSoloSelect from '../ui/FreeSoloSelect';
import {
  getMigrationHistory,
  executeMigration,
  rollbackMigration,
  previewMigration,
  canRollback,
} from 'common/src/api/migrations';
import { fetchUniqueValues } from 'common/src/api/others';
import {
  DataMigration,
  MigrationTargetTable,
  UniqueValueAnalysis,
} from 'common/src/utils/types';

// Configuration for editable columns by table
// Using translation keys instead of hardcoded Spanish text
const MIGRATION_CONFIG: Record<
  MigrationTargetTable,
  { nameKey: string; columns: { key: string; nameKey: string }[] }
> = {
  settlements_details: {
    nameKey: 'Detalles de Liquidaciones',
    columns: [
      { key: 'caliber', nameKey: 'Calibre' },
      { key: 'color', nameKey: 'Color' },
      { key: 'label', nameKey: 'Etiqueta' },
      { key: 'variety', nameKey: 'Variedad' },
    ],
  },
  shipment_boxes: {
    nameKey: 'Base de Despachos',
    columns: [
      { key: 'variety', nameKey: 'Variedad' },
      { key: 'labeled_variety', nameKey: 'Variedad Etiquetada' },
      { key: 'caliber', nameKey: 'Calibre' },
      { key: 'color', nameKey: 'Color' },
      { key: 'category', nameKey: 'Categoría' },
      { key: 'box_label', nameKey: 'Etiqueta' },
    ],
  },
};

export const DataMigrationSection = () => {
  const { t } = useTranslation('settings');
  const [location, setLocation] = useLocation();
  const { userProfile } = useUserStore();
  const { selectedSpecies, species } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const {
    labels: officialLabels,
    loading: labelsLoading,
    fetchLabels,
  } = useExporterLabelsStore();
  const officialLabelSet = useMemo(
    () =>
      new Set(
        officialLabels
          .map((label) => label.label.trim().toUpperCase())
          .filter((label) => label.length > 0)
      ),
    [officialLabels]
  );
  const officialVarietySet = useMemo(() => {
    const selected =
      species.find((item) => item.id === selectedSpecies?.id) ||
      selectedSpecies;
    return new Set(
      (selected?.all_varieties || [])
        .map((variety) => variety.trim().toUpperCase())
        .filter((variety) => variety.length > 0)
    );
  }, [selectedSpecies, species]);

  const [migrations, setMigrations] = useState<DataMigration[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRollbackModalOpen, setIsRollbackModalOpen] = useState(false);
  const [selectedMigrationToRollback, setSelectedMigrationToRollback] =
    useState<DataMigration | null>(null);

  // Create migration form state
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<number | null>(
    selectedSpecies?.id || null
  );
  const [selectedTable, setSelectedTable] = useState<MigrationTargetTable>(
    'settlements_details'
  );
  const [selectedColumn, setSelectedColumn] = useState('');
  const [uniqueValues, setUniqueValues] = useState<UniqueValueAnalysis[]>([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [oldValue, setOldValue] = useState('');
  const [newValue, setNewValue] = useState('');
  const [notes, setNotes] = useState('');
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [executing, setExecuting] = useState(false);
  const shouldSuggestOfficialLabels =
    selectedTable === 'settlements_details' && selectedColumn === 'label';
  const shouldSuggestOfficialVarieties =
    selectedTable === 'settlements_details' && selectedColumn === 'variety';
  const officialLabelOptions = useMemo(
    () =>
      officialLabels
        .map((label) => label.label.trim().toUpperCase())
        .filter((label) => label.length > 0)
        .sort((a, b) => a.localeCompare(b)),
    [officialLabels]
  );
  const officialVarietyOptions = useMemo(
    () => Array.from(officialVarietySet).sort((a, b) => a.localeCompare(b)),
    [officialVarietySet]
  );
  const newValueOptions = useMemo(() => {
    if (shouldSuggestOfficialLabels && officialLabelOptions.length > 0) {
      return officialLabelOptions
        .filter((label) => label !== oldValue)
        .map((label) => ({
          value: label,
          displayValue: label,
        }));
    }

    if (
      shouldSuggestOfficialVarieties &&
      officialVarietyOptions.length > 0
    ) {
      const officialOptions = officialVarietyOptions
        .filter((variety) => variety !== oldValue)
        .map((variety) => ({
          value: variety,
          displayValue: variety,
          isOfficial: true,
        }));

      const nonOfficialOptions = uniqueValues
        .filter((item) => {
          const normalized = item.value.trim().toUpperCase();
          return (
            item.value !== oldValue &&
            item.value !== '' &&
            !officialVarietySet.has(normalized)
          );
        })
        .map((item) => ({
          value: item.value,
          displayValue: item.value,
          count: item.count,
        }));

      return [...officialOptions, ...nonOfficialOptions];
    }

    return uniqueValues
      .filter((item) => item.value !== oldValue && item.value !== '')
      .map((item) => ({
        value: item.value,
        displayValue: item.value,
        count: item.count,
      }));
  }, [
    officialLabelOptions,
    officialVarietyOptions,
    officialVarietySet,
    oldValue,
    shouldSuggestOfficialLabels,
    shouldSuggestOfficialVarieties,
    uniqueValues,
  ]);

  // My Data tab states
  const [settlementsData, setSettlementsData] = useState<
    Record<string, UniqueValueAnalysis[]>
  >({});
  const [shipmentsData, setShipmentsData] = useState<
    Record<string, UniqueValueAnalysis[]>
  >({});
  const [loadingMyData, setLoadingMyData] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('my-data');

  const prefillMigrationForm = (
    table: MigrationTargetTable,
    column: string,
    value: string
  ) => {
    if (selectedSpecies?.id) {
      setSelectedSpeciesId(selectedSpecies.id);
    }
    setSelectedTable(table);
    setSelectedColumn(column);
    setOldValue(value);
    setNewValue('');
    setPreviewCount(null);
    setIsCreateModalOpen(true);
  };

  const goToSettingsHash = (hash: string) => {
    if (location.startsWith('/settings')) {
      if (window.location.hash !== hash) {
        window.location.hash = hash;
      } else {
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      }
      return;
    }
    setLocation(`/settings${hash}`);
  };

  const handlePrefillKeyDown = (
    event: React.KeyboardEvent,
    table: MigrationTargetTable,
    column: string,
    value: string
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      prefillMigrationForm(table, column, value);
    }
  };

  // Update selected species when global species changes
  useEffect(() => {
    if (selectedSpecies?.id) {
      setSelectedSpeciesId(selectedSpecies.id);
    }
  }, [selectedSpecies]);

  useEffect(() => {
    if (!userProfile?.exporter_id || !selectedSpecies?.id || !selectedSeason) {
      return;
    }
    fetchLabels(
      userProfile.exporter_id,
      selectedSpecies.id,
      selectedSeason
    ).catch(() => undefined);
  }, [
    fetchLabels,
    selectedSeason,
    selectedSpecies?.id,
    userProfile?.exporter_id,
  ]);

  // Reload "Mis Datos" when species or season changes (only if tab is active)
  useEffect(() => {
    if (activeTab === 'my-data' && selectedSpecies?.id && selectedSeason) {
      loadMyData();
    }
  }, [selectedSpecies?.id, selectedSeason, activeTab]);

  // Define functions before useEffect
  const loadMigrations = async () => {
    if (!userProfile?.exporter_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Load ALL migrations for the exporter (all species) to enforce LIFO correctly
      const data = await getMigrationHistory(userProfile.exporter_id);
      setMigrations(data);
    } catch (error) {
      toast.error(t('Error al cargar historial de estandarizaciones'));
      console.error('Migration history error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUniqueValues = async () => {
    if (!userProfile?.exporter_id || !selectedSpeciesId || !selectedSeason)
      return;

    try {
      setLoadingValues(true);
      const values = await fetchUniqueValues(selectedTable, selectedColumn, {
        exporterId: userProfile.exporter_id,
        speciesId: selectedSpeciesId,
        season: selectedSeason,
      });
      setUniqueValues(values);
    } catch (error) {
      console.error('Error loading unique values:', error);
      toast.error(t('Error al cargar valores únicos'));
    } finally {
      setLoadingValues(false);
    }
  };

  const loadMyData = async () => {
    if (!userProfile?.exporter_id || !selectedSpecies?.id || !selectedSeason)
      return;

    try {
      setLoadingMyData(true);

      const filters = {
        exporterId: userProfile.exporter_id,
        speciesId: selectedSpecies.id,
        season: selectedSeason,
      };

      // Load ALL columns in PARALLEL for maximum performance
      const settlementsColumns = MIGRATION_CONFIG.settlements_details.columns;
      const shipmentsColumns = MIGRATION_CONFIG.shipment_boxes.columns;

      // Create all promises at once
      const allPromises = [
        ...settlementsColumns.map((column) =>
          fetchUniqueValues('settlements_details', column.key, filters).then(
            (values) => ({ table: 'settlements', column: column.key, values })
          )
        ),
        ...shipmentsColumns.map((column) =>
          fetchUniqueValues('shipment_boxes', column.key, filters).then(
            (values) => ({ table: 'shipments', column: column.key, values })
          )
        ),
      ];

      // Execute all in parallel
      const results = await Promise.all(allPromises);

      // Process results
      const settlementsDataTemp: Record<string, UniqueValueAnalysis[]> = {};
      const shipmentsDataTemp: Record<string, UniqueValueAnalysis[]> = {};

      results.forEach((result) => {
        if (result.table === 'settlements') {
          settlementsDataTemp[result.column] = result.values;
        } else {
          shipmentsDataTemp[result.column] = result.values;
        }
      });

      setSettlementsData(settlementsDataTemp);
      setShipmentsData(shipmentsDataTemp);
    } catch (error) {
      console.error('Error loading my data:', error);
      toast.error(t('Error al cargar datos'));
    } finally {
      setLoadingMyData(false);
    }
  };

  const loadPreview = useCallback(async () => {
    if (!userProfile?.exporter_id || !selectedSpeciesId || !selectedSeason)
      return;

    try {
      console.log('Loading preview with:', {
        speciesId: selectedSpeciesId,
        targetTable: selectedTable,
        targetColumn: selectedColumn,
        oldValue,
        newValue,
        filterSeason: selectedSeason,
      });

      const count = await previewMigration(userProfile.exporter_id, {
        speciesId: selectedSpeciesId,
        targetTable: selectedTable,
        targetColumn: selectedColumn,
        oldValue,
        newValue,
        filterSeason: selectedSeason,
      });

      console.log('Preview count:', count);
      setPreviewCount(count);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error(t('Error al cargar preview de registros'));
      setPreviewCount(0);
    }
  }, [
    userProfile?.exporter_id,
    selectedSpeciesId,
    selectedSeason,
    selectedTable,
    selectedColumn,
    oldValue,
    newValue,
    t,
  ]);

  // useEffects after all functions are defined
  useEffect(() => {
    loadMigrations();
  }, [userProfile?.exporter_id]);

  useEffect(() => {
    if (selectedTable && selectedColumn) {
      loadUniqueValues();
    }
  }, [selectedTable, selectedColumn]);

  useEffect(() => {
    console.log('Preview useEffect triggered:', {
      oldValue,
      newValue,
      exporterId: userProfile?.exporter_id,
      selectedTable,
      selectedColumn,
    });

    if (oldValue && newValue && userProfile?.exporter_id) {
      console.log('Calling loadPreview...');
      loadPreview();
    } else {
      console.log('Resetting preview count');
      setPreviewCount(null);
    }
  }, [
    oldValue,
    newValue,
    selectedTable,
    selectedColumn,
    userProfile?.exporter_id,
    loadPreview,
  ]);

  const handleCreateMigration = async () => {
    if (!userProfile?.exporter_id || !selectedSpeciesId || !selectedSeason)
      return;
    if (!oldValue || !newValue) {
      toast.error(t('Debes seleccionar valores antiguo y nuevo'));
      return;
    }

    try {
      setExecuting(true);
      const result = await executeMigration(userProfile.exporter_id, {
        speciesId: selectedSpeciesId,
        targetTable: selectedTable,
        targetColumn: selectedColumn,
        oldValue,
        newValue,
        filterSeason: selectedSeason,
        notes: notes || undefined,
      });

      if (result.affectedCount === 0) {
        toast.warning(t('No se encontraron registros para actualizar'));
      } else {
        toast.success(
          t('Estandarización completada: {{count}} registros actualizados', {
            count: result.affectedCount,
          })
        );
      }

      // Reset form
      setOldValue('');
      setNewValue('');
      setNotes('');
      setPreviewCount(null);

      // Reload unique values to reflect the changes
      if (selectedTable && selectedColumn) {
        await loadUniqueValues();
      }

      // Reload "Mis Datos" to reflect changes immediately
      await loadMyData();

      window.dispatchEvent(new Event('sidebar-data-refresh'));

      setIsCreateModalOpen(false);

      // Reload migrations
      loadMigrations();
    } catch (error) {
      toast.error(t('Error al ejecutar estandarización'));
      console.error(error);
    } finally {
      setExecuting(false);
    }
  };

  const handleRollback = async () => {
    if (!selectedMigrationToRollback) return;

    try {
      setExecuting(true);

      // Check if can rollback
      const checkResult = await canRollback(selectedMigrationToRollback.id);
      if (!checkResult.canRollback) {
        toast.error(
          checkResult.reason || t('No se puede revertir esta estandarización')
        );
        setIsRollbackModalOpen(false);
        return;
      }

      const result = await rollbackMigration(selectedMigrationToRollback.id);

      if (result.success) {
        toast.success(
          t('Estandarización revertida: {{count}} registros restaurados', {
            count: result.revertedCount,
          })
        );
        setIsRollbackModalOpen(false);

        // Reload unique values to reflect the rollback changes
        if (selectedTable && selectedColumn) {
          await loadUniqueValues();
        }

        // Reload "Mis Datos" to reflect changes immediately
        await loadMyData();

        window.dispatchEvent(new Event('sidebar-data-refresh'));

        loadMigrations();
      } else {
        toast.error(result.error || t('Error al revertir estandarización'));
      }
    } catch (error) {
      toast.error(t('Error al revertir estandarización'));
      console.error(error);
    } finally {
      setExecuting(false);
      setSelectedMigrationToRollback(null);
    }
  };

  // Filter migrations by selected species AND season (client-side)
  const filteredMigrations = migrations.filter((m) => {
    const matchesSpecies = selectedSpeciesId
      ? m.species_id === selectedSpeciesId
      : true;
    const matchesSeason = selectedSeason
      ? m.filter_season === selectedSeason
      : true;
    return matchesSpecies && matchesSeason;
  });

  const isLatestCompletedMigration = (migrationId: number) => {
    // Use ALL migrations (not filtered) to enforce LIFO across all species and seasons
    const completedMigrations = migrations.filter(
      (m) => m.status === 'completed'
    );
    if (completedMigrations.length === 0) return false;

    const latest = completedMigrations.sort(
      (a, b) =>
        new Date(b.executed_at).getTime() - new Date(a.executed_at).getTime()
    )[0];

    return latest.id === migrationId;
  };

  return (
    <div>
      <div className='flex justify-between items-start mb-4'>
        <div>
          <h2 className='text-xl font-bold'>{t('Estandarización de Datos')}</h2>
          {selectedSpecies && selectedSeason && (
            <p className='text-sm text-default-400 mt-1'>
              {selectedSpecies.name} · {selectedSeason}
            </p>
          )}
        </div>
        <Button
          color='primary'
          onPress={() => setIsCreateModalOpen(true)}
        >
          {t('Nueva Estandarización')}
        </Button>
      </div>

      <Tabs
        aria-label={t('Pestañas de estandarización')}
        selectedKey={activeTab}
        onSelectionChange={(key) => {
          setActiveTab(key as string);
          if (key === 'my-data' && selectedSpecies?.id && selectedSeason) {
            loadMyData();
          }
        }}
      >
        <Tab key='my-data' title={t('Mis Datos')}>
          <div className='pt-4'>
            {!selectedSpecies || !selectedSeason ? (
              <EmptyState
                title={t(
                  'Selecciona una especie y temporada en el sidebar para ver tus datos'
                )}
              />
            ) : loadingMyData ? (
              <LoadingState />
            ) : Object.keys(settlementsData).length === 0 &&
              Object.keys(shipmentsData).length === 0 ? (
              <EmptyState
                title={t('No hay datos disponibles para esta especie y temporada')}
              />
            ) : (
              <div className='flex flex-col gap-6'>
                {/* Settlements Details Data */}
                <div>
                  <h3 className='text-lg font-semibold mb-3'>
                    {t('Detalles de Liquidaciones')}
                  </h3>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {MIGRATION_CONFIG.settlements_details.columns.map(
                      (column) => (
                        <div key={column.key} className='border rounded-lg p-4'>
                          <h4 className='font-medium mb-2'>
                            {t(column.nameKey)}
                          </h4>
                          {column.key === 'variety' &&
                            officialVarietySet.size > 0 &&
                            (settlementsData[column.key] || []).some(
                              (item) =>
                                item.value.trim().length > 0 &&
                                !officialVarietySet.has(
                                  item.value.trim().toUpperCase()
                                )
                            ) && (
                              <Alert variant='warning' className='mb-3'>
                                <div className='text-sm text-amber-700'>
                                  {t(
                                    'Si ves variedades fuera de oficiales, probablemente sea un error de escritura. Si es correcta, avísanos para agregarla.'
                                  )}
                                </div>
                              </Alert>
                            )}
                          {column.key === 'label' &&
                            !labelsLoading &&
                            officialLabelSet.size === 0 && (
                              <Alert variant='warning' className='mb-3'>
                                <div className='flex flex-wrap items-center justify-between gap-2'>
                                  <span>
                                    {t(
                                      'No tienes etiquetas oficiales definidas aún.'
                                    )}
                                  </span>
                                  <button
                                    type='button'
                                    onClick={() => goToSettingsHash('#labels')}
                                    className='text-sm font-medium text-amber-700 border border-amber-200 px-3 py-1.5 rounded-full hover:bg-amber-50'
                                  >
                                    {t('Declarar etiquetas')}
                                  </button>
                                </div>
                              </Alert>
                            )}
                          <div className='flex flex-wrap gap-2'>
                            {!settlementsData[column.key] ||
                            settlementsData[column.key].length === 0 ? (
                              <span className='text-sm text-default-300 italic'>
                                {t('No hay registros')}
                              </span>
                            ) : (
                              <>
                                {(() => {
                                  const highlightLabels =
                                    column.key === 'label' &&
                                    officialLabelSet.size > 0;
                                  const highlightVarieties =
                                    column.key === 'variety' &&
                                    officialVarietySet.size > 0;
                                  return settlementsData[column.key]
                                    .map((item) => {
                                      const displayValue = item.value
                                        .replace(/^ +/, (spaces) =>
                                          '_'.repeat(spaces.length)
                                        )
                                        .replace(/ +$/, (spaces) =>
                                          '_'.repeat(spaces.length)
                                        );
                                      const normalized = item.value
                                        .trim()
                                        .toUpperCase();
                                      const isOutside =
                                        (highlightLabels &&
                                          normalized.length > 0 &&
                                          !officialLabelSet.has(normalized)) ||
                                        (highlightVarieties &&
                                          normalized.length > 0 &&
                                          !officialVarietySet.has(normalized));
                                      const chip = (
                                        <span
                                          role='button'
                                          tabIndex={0}
                                          className='inline-flex'
                                          onClick={() =>
                                            prefillMigrationForm(
                                              'settlements_details',
                                              column.key,
                                              item.value
                                            )
                                          }
                                          onKeyDown={(event) =>
                                            handlePrefillKeyDown(
                                              event,
                                              'settlements_details',
                                              column.key,
                                              item.value
                                            )
                                          }
                                        >
                                          <Chip
                                            key={item.value}
                                            size='sm'
                                            variant='flat'
                                            className='cursor-pointer'
                                            color={
                                              isOutside ? 'danger' : 'default'
                                            }
                                          >
                                            <span className='font-mono text-xs'>
                                              {displayValue} ({item.count})
                                            </span>
                                          </Chip>
                                        </span>
                                      );

                                      return isOutside ? (
                                        <Tooltip
                                          key={item.value}
                                          content={t(
                                            highlightVarieties
                                              ? 'fuera de variedades oficiales'
                                              : 'fuera de etiquetas oficiales'
                                          )}
                                        >
                                          {chip}
                                        </Tooltip>
                                      ) : (
                                        chip
                                      );
                                    });
                                })()}
                              </>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Shipment Boxes Data */}
                <div>
                  <h3 className='text-lg font-semibold mb-3'>
                    {t('Base de Despachos')}
                  </h3>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {MIGRATION_CONFIG.shipment_boxes.columns.map((column) => (
                      <div key={column.key} className='border rounded-lg p-4'>
                        <h4 className='font-medium mb-2'>
                          {t(column.nameKey)}
                        </h4>
                        <div className='flex flex-wrap gap-2'>
                          {!shipmentsData[column.key] ||
                          shipmentsData[column.key].length === 0 ? (
                            <span className='text-sm text-default-300 italic'>
                              {t('No hay registros')}
                            </span>
                          ) : (
                            <>
                              {shipmentsData[column.key]
                                .map((item) => {
                                  const displayValue = item.value
                                    .replace(/^ +/, (spaces) =>
                                      '_'.repeat(spaces.length)
                                    )
                                    .replace(/ +$/, (spaces) =>
                                      '_'.repeat(spaces.length)
                                    );
                                  return (
                                    <span
                                      role='button'
                                      tabIndex={0}
                                      className='inline-flex'
                                      onClick={() =>
                                        prefillMigrationForm(
                                          'shipment_boxes',
                                          column.key,
                                          item.value
                                        )
                                      }
                                      onKeyDown={(event) =>
                                        handlePrefillKeyDown(
                                          event,
                                          'shipment_boxes',
                                          column.key,
                                          item.value
                                        )
                                      }
                                    >
                                      <Chip
                                        key={item.value}
                                        size='sm'
                                        variant='flat'
                                        className='cursor-pointer'
                                      >
                                        <span className='font-mono text-xs'>
                                          {displayValue} ({item.count})
                                        </span>
                                      </Chip>
                                    </span>
                                  );
                                })}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Tab>

        <Tab key='history' title={t('Historial')}>
          <div className='pt-4'>
            {loading ? (
              <LoadingState />
            ) : (
              <Table aria-label={t('Historial de estandarizaciones')}>
                <TableHeader>
                  <TableColumn>{t('FECHA')}</TableColumn>
                  <TableColumn>{t('USUARIO')}</TableColumn>
                  <TableColumn>{t('ESPECIE')}</TableColumn>
                  <TableColumn>{t('TEMPORADA')}</TableColumn>
                  <TableColumn>{t('TABLA')}</TableColumn>
                  <TableColumn>{t('COLUMNA')}</TableColumn>
                  <TableColumn>{t('CAMBIO')}</TableColumn>
                  <TableColumn>{t('REGISTROS')}</TableColumn>
                  <TableColumn>{t('ESTADO')}</TableColumn>
                  <TableColumn>{t('ACCIONES')}</TableColumn>
                </TableHeader>
                <TableBody
                  emptyContent={
                    <div className='text-center p-4 text-default-400'>
                      {t('No hay estandarizaciones registradas')}
                    </div>
                  }
                >
                  {filteredMigrations.map((migration) => (
                    <TableRow key={migration.id}>
                      <TableCell>
                        <div className='flex flex-col'>
                          <span className='text-sm'>
                            {format(
                              new Date(migration.executed_at),
                              'dd/MM/yyyy',
                              {
                                locale: es,
                              }
                            )}
                          </span>
                          <span className='text-xs text-default-400'>
                            {format(new Date(migration.executed_at), 'HH:mm', {
                              locale: es,
                            })}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='flex flex-col'>
                          <span className='text-sm'>
                            {migration.created_by_user
                              ? `${migration.created_by_user.first_name} ${migration.created_by_user.last_name}`
                              : t('Desconocido')}
                          </span>
                          {migration.status === 'rolled_back' &&
                            migration.rolled_back_by_user && (
                              <span className='text-xs text-default-400'>
                                {t('Revertido por')}:{' '}
                                {migration.rolled_back_by_user.first_name}{' '}
                                {migration.rolled_back_by_user.last_name}
                              </span>
                            )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className='text-sm'>
                          {migration.species?.name || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className='text-sm'>
                          {migration.filter_season || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className='text-sm'>
                          {t(MIGRATION_CONFIG[migration.target_table].nameKey)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className='text-sm'>
                          {t(
                            MIGRATION_CONFIG[
                              migration.target_table
                            ].columns.find(
                              (c) => c.key === migration.target_column
                            )?.nameKey || ''
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          <span className='text-sm font-mono bg-default-100 px-2 py-1 rounded'>
                            {migration.old_value
                              .replace(/^ +/, (spaces) =>
                                '_'.repeat(spaces.length)
                              )
                              .replace(/ +$/, (spaces) =>
                                '_'.repeat(spaces.length)
                              )}
                          </span>
                          <span>→</span>
                          <span className='text-sm font-mono bg-blue-100 px-2 py-1 rounded'>
                            {migration.new_value
                              .replace(/^ +/, (spaces) =>
                                '_'.repeat(spaces.length)
                              )
                              .replace(/ +$/, (spaces) =>
                                '_'.repeat(spaces.length)
                              )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className='text-sm'>
                          {migration.affected_records_count?.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        {migration.status === 'completed' ? (
                          <Chip color='success' size='sm' variant='flat'>
                            {t('Completada')}
                          </Chip>
                        ) : (
                          <Chip color='default' size='sm' variant='flat'>
                            {t('Revertida')}
                          </Chip>
                        )}
                      </TableCell>
                      <TableCell>
                        {migration.status === 'completed' && (
                          <Button
                            size='sm'
                            variant='flat'
                            isDisabled={
                              !isLatestCompletedMigration(migration.id)
                            }
                            onPress={() => {
                              setSelectedMigrationToRollback(migration);
                              setIsRollbackModalOpen(true);
                            }}
                          >
                            {t('Revertir')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </Tab>
      </Tabs>

      {/* Create Migration Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        size='2xl'
      >
        <ModalContent>
          <ModalHeader>{t('Nueva Estandarización de Datos')}</ModalHeader>
          <ModalBody>
            {/* Context indicator showing current species and season */}
            {selectedSpecies && selectedSeason && (
              <Alert className='mb-2'>
                <p>
                  <strong>{t('Especie')}:</strong> {selectedSpecies.name} ·{' '}
                  <strong>{t('Temporada')}:</strong> {selectedSeason}
                </p>
                <p className='text-xs font-normal mt-1'>
                  {t(
                    'Esta estandarización solo afectará registros de esta especie y temporada'
                  )}
                </p>
              </Alert>
            )}
            <div className='flex flex-col gap-4'>
              {/* Table selector */}
              <Select
                label={t('Tabla')}
                selectedKeys={[selectedTable]}
                onChange={(e) => {
                  setSelectedTable(e.target.value as MigrationTargetTable);
                  setSelectedColumn('');
                  setOldValue('');
                  setNewValue('');
                }}
                isDisabled={!selectedSpeciesId}
              >
                {Object.entries(MIGRATION_CONFIG).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    {t(value.nameKey)}
                  </SelectItem>
                ))}
              </Select>

              {/* Column selector */}
              <Select
                label={t('Columna')}
                selectedKeys={selectedColumn ? [selectedColumn] : []}
                onChange={(e) => {
                  setSelectedColumn(e.target.value);
                  setOldValue('');
                  setNewValue('');
                }}
                isDisabled={!selectedTable}
              >
                {MIGRATION_CONFIG[selectedTable].columns.map((column) => (
                  <SelectItem key={column.key} value={column.key}>
                    {t(column.nameKey)}
                  </SelectItem>
                ))}
              </Select>

              {/* Old value selector */}
              {selectedColumn && (
                <>
                  {loadingValues ? (
                    <LoadingState />
                  ) : (
                    <>
                      <Autocomplete
                        label={t('Valor Actual')}
                        placeholder={t('Selecciona el valor a cambiar')}
                        selectedKey={oldValue || null}
                        inputValue={
                          oldValue
                            ? oldValue
                                .replace(/^ +/, (spaces) =>
                                  '_'.repeat(spaces.length)
                                )
                                .replace(/ +$/, (spaces) =>
                                  '_'.repeat(spaces.length)
                                )
                            : undefined
                        }
                        onSelectionChange={(key) => {
                          console.log('Old value selected:', key);
                          setOldValue(key ? String(key) : '');
                        }}
                      >
                        {uniqueValues
                          .filter((item) => item.value !== '') // Exclude empty values
                          .map((item) => {
                            // Replace leading/trailing spaces with underscores for visibility
                            const displayValue = item.value
                              .replace(/^ +/, (spaces) =>
                                '_'.repeat(spaces.length)
                              )
                              .replace(/ +$/, (spaces) =>
                                '_'.repeat(spaces.length)
                              );

                            return (
                              <AutocompleteItem
                                key={item.value}
                                value={item.value}
                                textValue={displayValue}
                              >
                                <div className='flex justify-between items-center w-full'>
                                  <span className='font-mono'>
                                    {displayValue}
                                  </span>
                                  <span className='text-sm text-default-400 ml-2'>
                                    ({item.count.toLocaleString()})
                                  </span>
                                </div>
                              </AutocompleteItem>
                            );
                          })}
                      </Autocomplete>

                      {/* New value selector */}
                      <FreeSoloSelect
                        label={t('Nuevo Valor')}
                        placeholder={t('Selecciona o escribe el nuevo valor')}
                        value={newValue || ''}
                        onValueChange={(value) =>
                          setNewValue(value?.toUpperCase() || '')
                        }
                        options={newValueOptions}
                        isDisabled={!oldValue}
                      />
                    </>
                  )}
                </>
              )}

              {/* Preview */}
              {oldValue && newValue && (
                <div
                  className={`p-4 rounded-lg border ${
                    previewCount === null
                      ? 'bg-default-100 border-divider'
                      : previewCount > 0
                        ? 'bg-blue-50 dark:bg-blue-950 border-blue-200'
                        : 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200'
                  }`}
                >
                  {previewCount === null ? (
                    <p className='text-sm text-default-500'>
                      {t('Cargando preview...')}
                    </p>
                  ) : (
                    <>
                      <p className='text-sm font-medium text-blue-900'>
                        {t('Registros afectados')}:{' '}
                        {previewCount.toLocaleString()}
                      </p>
                      <p className='text-xs text-blue-700 mt-1 font-mono'>
                        {t(
                          'Se cambiarán {{count}} registros de "{{oldValue}}" a "{{newValue}}"',
                          {
                            count: previewCount,
                            oldValue: oldValue
                              .replace(/^ +/, (spaces) =>
                                '_'.repeat(spaces.length)
                              )
                              .replace(/ +$/, (spaces) =>
                                '_'.repeat(spaces.length)
                              ),
                            newValue: newValue
                              .replace(/^ +/, (spaces) =>
                                '_'.repeat(spaces.length)
                              )
                              .replace(/ +$/, (spaces) =>
                                '_'.repeat(spaces.length)
                              ),
                          }
                        )}
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Notes */}
              <Textarea
                label={t('Notas (opcional)')}
                placeholder={t('Describe el motivo de esta estandarización...')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxRows={3}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => setIsCreateModalOpen(false)}
            >
              {t('Cancelar')}
            </Button>
            <Button
              color='primary'
              onPress={handleCreateMigration}
              isDisabled={
                !oldValue || !newValue || previewCount === null || executing
              }
              isLoading={executing}
            >
              {t('Ejecutar Estandarización')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Rollback Confirmation Modal */}
      <Modal
        isOpen={isRollbackModalOpen}
        onClose={() => setIsRollbackModalOpen(false)}
      >
        <ModalContent>
          <ModalHeader>{t('Confirmar Reversión')}</ModalHeader>
          <ModalBody>
            {selectedMigrationToRollback && (
              <div className='flex flex-col gap-3'>
                <p>
                  {t(
                    '¿Estás seguro de que deseas revertir esta estandarización?'
                  )}
                </p>
                <div className='p-3 bg-default-100 rounded-lg'>
                  <p className='text-sm'>
                    <strong>{t('Tabla')}:</strong>{' '}
                    {t(
                      MIGRATION_CONFIG[selectedMigrationToRollback.target_table]
                        .nameKey
                    )}
                  </p>
                  <p className='text-sm'>
                    <strong>{t('Columna')}:</strong>{' '}
                    {t(
                      MIGRATION_CONFIG[
                        selectedMigrationToRollback.target_table
                      ].columns.find(
                        (c) =>
                          c.key === selectedMigrationToRollback.target_column
                      )?.nameKey || ''
                    )}
                  </p>
                  <p className='text-sm'>
                    <strong>{t('Cambio')}:</strong>{' '}
                    <span className='font-mono'>
                      {selectedMigrationToRollback.new_value
                        .replace(/^ +/, (spaces) => '_'.repeat(spaces.length))
                        .replace(/ +$/, (spaces) => '_'.repeat(spaces.length))}
                    </span>{' '}
                    →{' '}
                    <span className='font-mono'>
                      {selectedMigrationToRollback.old_value
                        .replace(/^ +/, (spaces) => '_'.repeat(spaces.length))
                        .replace(/ +$/, (spaces) => '_'.repeat(spaces.length))}
                    </span>
                  </p>
                  <p className='text-sm'>
                    <strong>{t('Registros')}:</strong>{' '}
                    {selectedMigrationToRollback.affected_records_count?.toLocaleString()}
                  </p>
                </div>
                <p className='text-sm text-warning-600'>
                  {t(
                    'Esta acción restaurará los valores originales de todos los registros afectados.'
                  )}
                </p>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => setIsRollbackModalOpen(false)}
            >
              {t('Cancelar')}
            </Button>
            <Button
              color='danger'
              onPress={handleRollback}
              isLoading={executing}
            >
              {t('Revertir Estandarización')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};
